import React, { ComponentProps, useMemo } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { SalesCredit } from '../../graphql/entities';
import Formatters from '../../components/grid-addons/formatters';
import customerAttributeQuery from '../../graphql/queries/customer/CustomerAttributeConfigListQuery.graphql';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { useAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import { Direction, ISalesCredit, Maybe } from '../../graphql/schema/p4types';
import { Paths } from '../../@types/keyOfNested';
import { useSalesCreditWorkspaceActions } from '../../actions/salesLedger/SalesCreditWorkspaceActions';
import { WorkspaceActionButtons } from '../../components/Workspace/WorkspaceActionButtons';
import { ICustomColumn } from '../../utils/setColumnsFromArray';
import { EntityType } from '../../@types/entityTypes';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';

const LazyCustomerForm = React.lazy(() => import('../../views/customer/CustomerForm'));

const SALES_CREDIT_FIELD_NAMES: Array<Paths<ISalesCredit>> = [
  'documentNumber',
  'documentDate',
  'documentStatus',
  'customer.reference',
  'customer.name',
  'documentReference',
  'headerTotal',
];

const SALES_CREDIT_COLUMN_OVERRIDES = {
  documentNumber: {
    name: 'Number',
    key: 'documentNumber',
    width: 100,
  },
  documentDate: {
    name: 'Date',
    key: 'documentDate',
    formatter: Formatters.DateFormatter,
    width: 110,
  },
  headerTotal: {
    name: 'Credit Total',
    width: 150,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
  },
  documentStatus: {
    name: 'Status',
    width: 80,
    formatter: Formatters.DocumentStatusFormatter,
  },
  'customer.reference': {
    name: 'Customer',
    formatter: ModalFormatter,
    width: 125,
    options: {
      modalContent: LazyCustomerForm,
      modalProps: { forceReadOnly: true },
      entityId: 'customer.id',
    },
  },
  'customer.customerCreditDocumentDefault.output': {
    name: 'Document Output',
  },
  'deliveryAddress.country.description': {
    name: 'Delivery - Country Name',
  },
  paymentDueDate: {
    name: 'Credit Due Date',
  },
  'customer.customerAccountManagementGroup.description': {
    name: 'Customer Account Management Group',
  },
  'deliveryCustomer.customerAccountManagementGroup.description': {
    name: 'Delivery Customer Account Management Group',
  },
};

const SalesCreditWorkspaceActions = (props: ComponentProps<typeof WorkspaceActionButtons>) => {
  const [salesCreditWorkspaceActions, salesCreditActionModals] = useSalesCreditWorkspaceActions(); //Contents of this hook defined in essentially the same manner as for form actions.
  return (
    <>
      {salesCreditActionModals}
      <WorkspaceActionButtons {...props} actionButtons={salesCreditWorkspaceActions} />
    </>
  );
};

const filtersFilter = (column: IEditableDataGridColumnDef) => {
  return (
    column?.fieldName !== 'deliveryAddress.line1' &&
    column?.fieldName !== 'deliveryAddress.line2' &&
    column?.fieldName !== 'deliveryAddress.line3' &&
    column?.fieldName !== 'deliveryAddress.line4' &&
    column?.fieldName !== 'deliveryAddress.line5' &&
    column?.fieldName !== 'deliveryAddress.postCode' &&
    column?.fieldName !== 'deliveryAddress.country' &&
    column?.fieldName !== 'deliveryName' &&
    column?.fieldName !== 'deliveryPhoneNumber' &&
    column?.fieldName !== 'deliveryMobileNumber' &&
    column?.fieldName !== 'deliveryEmail' &&
    column?.fieldName !== 'invoiceAddress.line1' &&
    column?.fieldName !== 'invoiceAddress.line2' &&
    column?.fieldName !== 'invoiceAddress.line3' &&
    column?.fieldName !== 'invoiceAddress.line4' &&
    column?.fieldName !== 'invoiceAddress.line5' &&
    column?.fieldName !== 'invoiceAddress.postCode' &&
    column?.fieldName !== 'invoiceAddress.country' &&
    column?.fieldName !== 'invoiceAddress.country.isoCountry' &&
    column?.fieldName !== 'invoiceCompany'
  );
};

const SalesCreditWorkspace = (
  props: Parameters<typeof useWorkspace>[0] &
    ComponentProps<typeof Workspace> & {
      entity?: EntityType;
      customColumns?: Maybe<{ [key: string]: ICustomColumn }>;
    }
) => {
  const { readOnlyStatus, formUrl, type, title, workspace } = props.entity || SalesCredit;

  const customerAttributeColumns = useAttributesForWorkspace(customerAttributeQuery, {
    valuesPath: 'customer.attributeValues',
    columnKey: 'customer.attributeValues.attributeConfig.title',
  });

  const customColumns = useMemo(
    () => ({ ...(props.customColumns || {}), ...customerAttributeColumns }),
    [props.customColumns, customerAttributeColumns]
  );

  const workspaceProps = useWorkspace<ISalesCredit>(props, SalesCredit, {
    columns: SALES_CREDIT_FIELD_NAMES,
    customColumns: customColumns,
    columnOverrides: SALES_CREDIT_COLUMN_OVERRIDES,
    initialSortColumn: 'documentNumber',
    initialSortDirection: Direction.Desc,
  });

  return (
    <Workspace
      entityType={type}
      title={title}
      formUrl={formUrl}
      defaultQuery={workspace}
      readOnlyStatus={readOnlyStatus}
      {...workspaceProps}
      {...props}
      entity={props.entity || SalesCredit}
      availableFiltersFilter={filtersFilter}
      workspaceActionButtonsHOC={SalesCreditWorkspaceActions}
    />
  );
};

export default SalesCreditWorkspace;
